/**
 * Connection Status Component
 * Displays a banner when the backend is offline or when connection is restored
 * Prevents user confusion by showing clear connection status
 */

import React, { useState, useEffect } from 'react';
import { connectionHealthService } from '../services/connectionHealthService';

const ConnectionStatus = () => {
  const [status, setStatus] = useState({
    isOnline: true,
    showBanner: false,
    message: '',
    offlineDuration: 0
  });

  // Track mobile viewport for responsive sizing (P1 fix: respond to resize)
  const [isMobileViewport, setIsMobileViewport] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    // Initial status
    const currentStatus = connectionHealthService.getStatus();
    if (!currentStatus.isOnline) {
      setStatus({
        isOnline: false,
        showBanner: true,
        message: 'Backend is offline. Attempting to reconnect...',
        offlineDuration: currentStatus.offlineDuration
      });
    }

    // Subscribe to status changes
    const unsubscribe = connectionHealthService.subscribe(({ isOnline, wasOnline }) => {
      if (!isOnline && wasOnline) {
        // Just went offline
        setStatus({
          isOnline: false,
          showBanner: true,
          message: 'Backend is offline. Attempting to reconnect...',
          offlineDuration: 0
        });
      } else if (isOnline && !wasOnline) {
        // Just came back online
        setStatus({
          isOnline: true,
          showBanner: true,
          message: 'Connection restored!',
          offlineDuration: 0
        });

        // Hide success banner after 5 seconds
        setTimeout(() => {
          setStatus(prev => ({ ...prev, showBanner: false }));
        }, 5000);
      }
    });

    // Update offline duration every 10 seconds when offline
    const durationInterval = setInterval(() => {
      const currentStatus = connectionHealthService.getStatus();
      if (!currentStatus.isOnline) {
        setStatus(prev => ({
          ...prev,
          offlineDuration: currentStatus.offlineDuration
        }));
      }
    }, 10000);

    return () => {
      unsubscribe();
      clearInterval(durationInterval);
    };
  }, []);

  // Listen for window resize to update mobile viewport state (P1 fix)
  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!status.showBanner) {
    return null;
  }

  const formatDuration = ms => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: isMobileViewport ? '6px 12px' : '12px 20px',
        backgroundColor: status.isOnline ? '#10b981' : '#ef4444',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: isMobileViewport ? '12px' : '14px',
        fontWeight: '500'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobileViewport ? '8px' : '12px' }}>
        {!status.isOnline && (
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'white',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          />
        )}
        <span>
          {status.message}
          {!status.isOnline && status.offlineDuration > 0 && (
            <span style={{ marginLeft: '8px', opacity: 0.9 }}>
              (Offline for {formatDuration(status.offlineDuration)})
            </span>
          )}
        </span>
        {status.isOnline && (
          <button
            onClick={() => setStatus(prev => ({ ...prev, showBanner: false }))}
            style={{
              marginLeft: '12px',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 4px',
              opacity: 0.8
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default ConnectionStatus;
